import Sortable from 'sortablejs';

// Drag-to-reorder shared by the kit's two sortable containers
// (Ui::AccordionComponent and Ui::DraggableListComponent). Both have the same
// shape — a flat list of rows where some are pinned — so the coordination lives
// here once instead of being copied per component:
//
// * SortableJS setup, with the controls inside a row kept clickable.
// * Fencing: a row can only be dropped among the movable rows adjacent to it,
//   so pinned rows stay put and nothing can be dragged past them.
// * Alt + up/down as the keyboard equivalent of a drag, announced in a live
//   region, because a pointer-only reorder is unusable with a keyboard or a
//   screen reader.
// * Renumbering the badges by position, batched into one frame so a page load
//   or a turbo stream that swaps several rows renumbers once.
//
// The owning controller supplies the DOM knowledge (which rows, which badge,
// what a row is called) and receives `onReorder` when a move changes the order.
export default class SortableList {
  constructor({
    element,
    movableSelector,
    handle = null,
    chosenClass,
    ghostClass,
    fallbackClass = 'ui-sortable-clone',
    hideInClone = null,
    parkSelector = null,
    ignoreAttribute,
    drag = true,
    rows,
    badgeFor,
    titleFor = () => 'Item',
    status = () => null,
    numberBase = () => 1,
    onReorder,
  }) {
    Object.assign(this, {
      element, movableSelector, rows, badgeFor, titleFor, status, numberBase, onReorder,
      handle, ghostClass, fallbackClass, hideInClone, parkSelector,
    });

    this.reachable = [];
    if (!drag) return;

    this.sortable = Sortable.create(element, {
      draggable: movableSelector,
      // One list only; also stops Sortable from parking a hidden clone among the rows.
      group: { pull: false, put: false },
      ...(handle ? { handle } : {}),
      // Keep each row's buttons, links and help tooltip clickable.
      filter: `a, button, input, select, textarea, [${ignoreAttribute}]`,
      preventOnFilter: false,
      // Native HTML5 drag and drop aborts as soon as Sortable re-inserts the
      // dragged element, so a drag could only ever move one position. The
      // fallback drives it from pointer events instead.
      forceFallback: true,
      // The clone that trails the cursor goes on <body>, not inside the
      // container, where it would shove the rows around.
      fallbackOnBody: true,
      animation: 150,
      chosenClass,
      ghostClass,
      fallbackClass,
      onStart: (event) => {
        this.lifted(event.item);
        this.defuseClone();
      },
      onMove: (event) => this.reachable.includes(event.related),
      onEnd: (event) => {
        this.reviveNested();
        this.dropped(event);
      },
    });

    // A nested Sortable inside the row being dragged corrupts SortableJS's
    // module-level drag state and the drag seizes up, so a caller whose rows
    // can contain one names it in `parkSelector`: it is parked the moment the
    // handle is pressed, before the drag starts on the following move, and
    // Stimulus tears it down (its observer runs first) so the drag operates on
    // a plain row. Parking in `onStart` would be too late — the drag has begun.
    //
    // Only those: the handle is also the row's click surface, so anything
    // parked here is torn down and rebooted by an ordinary click, which would
    // throw away whatever state its controller was holding.
    if (!parkSelector) return;

    this.element.addEventListener('pointerdown', this.parkNested, true);
    // On the document, so a release anywhere restores them — the pointer often
    // leaves the row mid-drag, and a cancelled press never reaches `pointerup`
    // at all. `onEnd` calls it too, and every path is idempotent.
    const doc = this.element.ownerDocument;
    doc.addEventListener('pointerup', this.reviveNested, true);
    doc.addEventListener('pointercancel', this.reviveNested, true);
    doc.defaultView?.addEventListener('blur', this.reviveNested);
  }

  parkNested = (event) => {
    // Only a press on the handle can begin a drag.
    if (this.parked || (this.handle && !event.target.closest?.(this.handle))) return;

    const row = event.target.closest?.(this.movableSelector);
    if (!row) return;

    this.parked = [...row.querySelectorAll(this.parkSelector)].map((node) => {
      const value = node.getAttribute('data-controller');
      node.removeAttribute('data-controller');
      return [node, value];
    });
  };

  reviveNested = () => {
    this.parked?.forEach(([node, value]) => node.setAttribute('data-controller', value));
    this.parked = null;
  };

  // The clone that trails the cursor is a deep copy of the row, so it would
  // carry a second set of those controllers and a duplicate of every id. Strip
  // them the instant it appears, before Stimulus's observer can boot anything
  // on it, along with whatever the drag has no reason to show.
  defuseClone() {
    const clone = this.element.ownerDocument.querySelector(`.${this.fallbackClass}`);
    if (!clone) return;

    if (this.hideInClone) clone.querySelector(this.hideInClone)?.remove();
    clone.querySelectorAll('[data-controller], [id]').forEach((node) => {
      node.removeAttribute('data-controller');
      node.removeAttribute('id');
    });
  }

  destroy() {
    const doc = this.element.ownerDocument;
    this.element.removeEventListener('pointerdown', this.parkNested, true);
    doc.removeEventListener('pointerup', this.reviveNested, true);
    doc.removeEventListener('pointercancel', this.reviveNested, true);
    doc.defaultView?.removeEventListener('blur', this.reviveNested);
    this.reviveNested();
    this.sortable?.destroy();
    this.sortable = null;
    if (this.renumberFrame) cancelAnimationFrame(this.renumberFrame);
    this.renumberFrame = null;
  }

  // Rows connect one by one, so a page load or a turbo stream that swaps
  // several would otherwise renumber once per row.
  scheduleRenumber() {
    if (this.renumberFrame) return;

    this.renumberFrame = requestAnimationFrame(() => {
      this.renumberFrame = null;
      this.renumber();
    });
  }

  // Rows without a number badge (e.g. an icon badge) take no position.
  renumber() {
    let position = this.numberBase();

    this.rows().forEach((row) => {
      const badge = this.badgeFor(row);
      if (badge) badge.textContent = String(position++);
    });
  }

  // The keyboard equivalent of a drag: Alt + arrow moves the row holding focus
  // one place within the run of movable rows it is fenced into.
  reorderKeydown(event, rowFor) {
    if (!event.altKey) return;

    const step = { ArrowUp: -1, ArrowDown: 1 }[event.key];
    if (!step) return;

    const { row, focus } = rowFor(event) || {};
    if (!row || !this.rows().includes(row)) return;

    event.preventDefault();
    this.move(row, step);
    focus?.focus();
  }

  move(row, step) {
    const neighbours = this.movableNeighbours(row);
    const target = neighbours[neighbours.indexOf(row) + step];
    if (!target) return;

    const oldIndex = this.rows().indexOf(row);
    if (step < 0) target.before(row); else target.after(row);
    this.reordered(row, oldIndex);
  }

  // The run of consecutive movable rows around `row`, bounded by pinned ones.
  movableNeighbours(row) {
    const rows = this.rows();
    const movable = (index) => rows[index]?.matches(this.movableSelector);
    let first = rows.indexOf(row);
    let last = first;

    while (movable(first - 1)) first -= 1;
    while (movable(last + 1)) last += 1;

    return rows.slice(first, last + 1);
  }

  lifted(row) {
    this.reachable = this.movableNeighbours(row);
    this.liftedIndex = this.rows().indexOf(row);
  }

  dropped(event) {
    // Strip the ghost ourselves: the turbo replace that follows a reorder can
    // race Sortable's own cleanup and strand the class on the row.
    event.item.classList.remove(this.ghostClass);
    const oldIndex = this.liftedIndex;
    this.reachable = [];
    this.reordered(event.item, oldIndex);
  }

  // Indices count every row, unlike Sortable's, which skip the pinned ones.
  reordered(row, oldIndex) {
    this.renumber();

    const rows = this.rows();
    const newIndex = rows.indexOf(row);
    if (oldIndex === newIndex) return;

    this.announce(row, newIndex, rows.length);
    this.onReorder({ item: row, oldIndex, newIndex, order: rows.map((each) => each.id) });
  }

  announce(row, index, total) {
    const region = this.status();
    if (!region) return;

    region.textContent =
      `${this.titleFor(row)} moved to position ${index + this.numberBase()} of ${total}`;
  }
}
